import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useSettings } from '../../contexts/SettingsContext';
import { useTopbar } from '../../contexts/TopbarContext';
import ThemeSwitcher from '../ThemeSwitcher/ThemeSwitcher';
import { NotificationCenter } from '../NotificationCenter';
import { useUnreadCount } from '../../hooks/useNotifications';
import FavoritesTopbar, { FavoritesDisplayMode } from './FavoritesTopbar';
import CombinedSearch from './CombinedSearch';
import UserAccountMenu from './UserAccountMenu';
import { useUserSettings } from '../../modules/settings/useData';
import { useModuleEntitlements } from '../../modules/module-management';
import { cn } from '../../lib/utils';

interface TopbarProps {
  title?: string;
  subtitle?: string;
  icon?: React.ReactNode;
  onToggleMobileMenu?: () => void;
  isMobileMenuOpen?: boolean;
  /** When true (used by mobile layout), hide favorites/topbar extras */
  hideFavorites?: boolean;
  hideThemeSwitcher?: boolean;
  hideSearch?: boolean;
  hideNotifications?: boolean;
   /** When true (used by mobile layout), keep only Sign out in user menu and hide extra action buttons */
   simpleUserMenu?: boolean;
}

const Topbar: React.FC<TopbarProps> = ({
  title,
  subtitle: staticSubtitle,
  icon,
  onToggleMobileMenu,
  isMobileMenuOpen = false,
  hideFavorites = false,
  hideThemeSwitcher = false,
  hideSearch = false,
  hideNotifications = false,
   simpleUserMenu = false,
}) => {
  const { subtitle: dynamicSubtitle, titleOverride, rightActions } = useTopbar();
  const subtitle = dynamicSubtitle || staticSubtitle;
  const displayTitle = titleOverride ?? title;
  const { user } = useAuth();
  const { colors } = useTheme();
  const { appConfig } = useSettings();
  const navigate = useNavigate();
  const isCompact = false;
  const [showNotifications, setShowNotifications] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const { count: unreadCount } = useUnreadCount();
  const { userSettings } = useUserSettings();
  const { entitlements } = useModuleEntitlements();

  // Determine favorites display mode - default to 'sidebar' (not shown in topbar)
  // On mobile/tablet, auto-switch from pins to dropdown for better UX
  const userPreferredMode = userSettings?.favoritesDisplayMode;
  const isTopMenuLayout = appConfig.layout.sidebarStyle === 'topmenu';
  const favoritesDisplayMode: FavoritesDisplayMode = hideFavorites
    ? 'none'
    : isTopMenuLayout
      ? 'none'
      : (userPreferredMode === 'sidebar' || userPreferredMode === 'none')
        ? 'none'
        : isCompact && userPreferredMode === 'pins'
          ? 'dropdown' // Auto-switch pins to dropdown on mobile/tablet
          : (userPreferredMode || 'none');

  // Update document title with app name
  useEffect(() => {
    if (appConfig.system.appName) {
      const pageTitle = displayTitle ?? title;
      document.title = pageTitle
        ? `${pageTitle} - ${appConfig.system.appName}` 
        : appConfig.system.appName;
    }
  }, [displayTitle, title, appConfig.system.appName]);

  const now = new Date();
  const supportTaskDefaults = {
    taskAssignedFrom: user?.id,
    taskAssignedTo: user?.id,
    taskCategoryId: 2,
    taskStatus: 1,
    taskDate: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`,
    taskStartDate: now.toISOString(),
  };

  const addMenuGroups = [
    { moduleGroupKey: 'tasks_support', label: 'Tasks', items: [
        { label: 'Task', href: '/crm_tasks/new', icon: 'task', iconBg: 'bg-teal-100 dark:bg-teal-500/20', iconColor: 'text-teal-700 dark:text-teal-400' },
        { label: 'Support Task', href: '/crm_tasks/new', state: { supportTaskDefaults }, icon: 'support', iconBg: 'bg-amber-100 dark:bg-amber-500/20', iconColor: 'text-amber-600 dark:text-amber-400' },
        { label: 'Todo', href: '/todos/new', icon: 'todo', iconBg: 'bg-emerald-100 dark:bg-emerald-500/20', iconColor: 'text-emerald-600 dark:text-emerald-400' },
      ] },
    { moduleGroupKey: 'sales_marketing', label: 'CRM', items: [
        { label: 'Client', href: '/clients/new', icon: 'client', iconBg: 'bg-sky-100 dark:bg-sky-500/20', iconColor: 'text-sky-600 dark:text-sky-400' },
        { label: 'Call', href: '/calls/new', icon: 'call', iconBg: 'bg-teal-100 dark:bg-teal-500/20', iconColor: 'text-teal-600 dark:text-teal-400' },
        { label: 'Appointment', href: '/appointments/new', icon: 'appointment', iconBg: 'bg-sky-100 dark:bg-sky-500/20', iconColor: 'text-sky-600 dark:text-sky-400' },
      ] },
  ];
  const filteredAddMenuGroups = addMenuGroups.filter(
    (group) => (group as { moduleGroupKey?: string }).moduleGroupKey == null || entitlements[(group as { moduleGroupKey: string }).moduleGroupKey] !== false
  );

  return (
    <header className={`${colors.topbarBackground} border-b ${colors.topbarBorder} sticky top-0 z-30`}>
      <div className={`flex items-center justify-between ${isCompact ? 'px-3 py-2.5' : 'px-5 py-3'}`}>
        {/* Left section: Menu toggle, icon, title */}
        <div className={`flex items-center ${isCompact ? 'space-x-2' : 'space-x-3'} min-w-0 flex-shrink-0`}>
          {onToggleMobileMenu && (
            <button
              onClick={onToggleMobileMenu}
              className={`lg:hidden p-2 rounded-lg ${colors.textMuted} hover:${colors.text} ${colors.interactiveHover} focus:outline-none focus:ring-2 focus:ring-teal-600/40`}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          )}
          {icon && (
            <div className={`${colors.textSecondary} ${isCompact ? 'flex-shrink-0' : ''}`}>
              {icon}
            </div>
          )}
          {displayTitle ? (
            <div className="min-w-0">
              <h1 className={`${isCompact ? 'text-lg' : 'text-xl'} font-bold font-display tracking-tight ${colors.text} truncate`}>
                {displayTitle}
              </h1>
              {subtitle && !isCompact && (
                <p className={`text-sm ${colors.textMuted} mt-0.5 truncate`}>
                  {subtitle}
                </p>
              )}
            </div>
          ) : null}
        </div>
        
        {/* Center section: Combined search (pages + records) + optional Favorite Pins */}
        <div className="flex-1 flex justify-center items-center gap-4 min-w-0 px-4">
          <CombinedSearch />
          {favoritesDisplayMode === 'pins' && (
            <FavoritesTopbar displayMode="pins" maxPins={7} />
          )}
        </div>
        
        {/* Right section: Add dropdown, Search, dropdown favorites, notifications, user menu */}
        <div className={`flex items-center ${isCompact ? 'space-x-2' : 'space-x-4'} flex-shrink-0`}>
          {/* Add dropdown (hidden in simpleUserMenu/mobile mode) */}
          {!simpleUserMenu && (
            <div className="relative">
              <button
                onClick={() => setShowAddMenu(!showAddMenu)}
                className={cn(
                  'relative flex items-center justify-center rounded-lg transition-colors',
                  isCompact ? 'p-2 min-w-[44px] min-h-[44px]' : 'p-2',
                  colors.textMuted,
                  `hover:${colors.textSecondary}`,
                  'hover:bg-black/5 dark:hover:bg-white/5',
                  'focus:outline-none focus:ring-2 focus:ring-teal-600/40'
                )}
                title="Quick add"
              >
                <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <svg className={cn('w-4 h-4 ml-0.5 shrink-0 transition-transform', showAddMenu && 'rotate-180')} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showAddMenu && (
                <>
                  <div className="fixed inset-0 z-10" aria-hidden onClick={() => setShowAddMenu(false)} />
                  <div
                    className={cn(
                      'absolute left-0 mt-2.5 w-72 z-20 overflow-hidden',
                      'rounded-2xl shadow-2xl border backdrop-blur-xl',
                      'animate-in fade-in slide-in-from-top-2 duration-200',
                      colors.surface,
                      colors.border
                    )}
                  >
                    <div className={cn('px-4 pt-4 pb-3', colors.border, 'border-b')}>
                      <div className="flex items-center gap-2">
                        <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-teal-500/15 text-teal-700 dark:text-teal-400')}>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                        </span>
                        <div>
                          <p className={cn('text-sm font-bold', colors.text)}>Create new</p>
                          <p className={cn('text-xs', colors.textMuted)}>Task, CRM item, or todo</p>
                        </div>
                      </div>
                    </div>
                    <div className="p-2">
                      {filteredAddMenuGroups.map((group) => (
                        <div key={group.label} className="mb-3 last:mb-0">
                          <p className={cn('px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest', colors.textMuted)}>
                            {group.label}
                          </p>
                          <div className="space-y-0.5">
                            {group.items.map((item) => (
                              <button
                                key={item.label}
                                type="button"
                                onClick={() => {
                                  setShowAddMenu(false);
                                  navigate(item.href, item.state ? { state: item.state } : undefined);
                                }}
                                className={cn(
                                  'flex items-center gap-3 w-full text-left px-3 py-2.5 rounded-xl text-sm font-medium transition-colors',
                                  'border-l-2 border-transparent hover:border-teal-500 hover:bg-teal-500/5 dark:hover:bg-teal-500/10',
                                  colors.text,
                                  colors.interactiveHover
                                )}
                              >
                                <span className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-xl', item.iconBg, item.iconColor)}>
                                {item.icon === 'task' && (
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                  </svg>
                                )}
                                {item.icon === 'support' && (
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                                  </svg>
                                )}
                                {item.icon === 'todo' && (
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                  </svg>
                                )}
                                {item.icon === 'client' && (
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                  </svg>
                                )}
                                {item.icon === 'call' && (
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V21a2 2 0 01-2 2h-1C9.716 23 3 16.284 3 8V5z" />
                                  </svg>
                                )}
                                {item.icon === 'appointment' && (
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                )}
                                </span>
                                <span>{item.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Theme Switcher - only show if enabled and not hidden (e.g. mobile layout) */}
          {!hideThemeSwitcher && appConfig.features.enableDarkMode && <ThemeSwitcher />}
          
          {/* Favorites Dropdown - only show dropdown mode in this area */}
          {favoritesDisplayMode === 'dropdown' && (
            <FavoritesTopbar displayMode="dropdown" />
          )}
          
          {/* Notifications */}
          {!hideNotifications && (
            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className={`relative ${isCompact ? 'p-2 min-w-[44px] min-h-[44px]' : 'p-2'} ${colors.textMuted} hover:${colors.textSecondary} focus:outline-none focus:ring-2 focus:ring-teal-600/40 rounded-lg flex items-center justify-center`}
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {unreadCount > 0 && (
                  <span className="absolute top-0 right-0 block h-5 w-5 rounded-full bg-red-500 text-white text-xs font-semibold flex items-center justify-center ring-2 ring-white">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
              {showNotifications && (
                <NotificationCenter onClose={() => setShowNotifications(false)} />
              )}
            </div>
          )}

          {/* User / Settings menu */}
          <UserAccountMenu placement="topbar" signOutOnly={simpleUserMenu} />
        </div>
      </div>
      
      {/* Favorites Bar - shown below main topbar content */}
      {favoritesDisplayMode === 'bar' && (
        <FavoritesTopbar displayMode="bar" />
      )}
    </header>
  );
};

export default Topbar;

